/* eslint-disable no-debugger */
/* eslint-disable @stylistic/padding-line-between-statements */
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { mutate } from 'swr';
import { clearUserCache } from '../users/userCacheService';
import {
	forgotPasswordService,
	LoginPayload,
	loginService,
	loginWithGoogleService,
	logoutService,
	refreshTokenService,
	RegisterPayload,
	registerService,
	registerWithGoogleService,
	resetPasswordWithTokenService,
	validateResetPasswordTokenService,
} from './authService';
import { useAuthStore } from './authStore';

import { useState } from 'react';

// Type for JWT token payload
interface DecodedToken {
	aud: string;
	email: string;
	exp: number;
	iat: number;
	iss: string;
	role: string;
	sub: string;
	isCompany?: boolean;
}

export function useLogin() {
	const { setAccessToken } = useAuthStore();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const login = async (payload: LoginPayload) => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await loginService({ email: payload.email, password: payload.password });
			const { accessToken, refreshToken } = response.data;

			// Decode the JWT token to check user role
			try {
				const decodedToken = jwtDecode<DecodedToken>(accessToken);

				// Check if user is admin
				if (decodedToken.role.toLowerCase() === 'admin') {
					// For admin users, save tokens and redirect to admin page
					setAccessToken(accessToken, refreshToken);

					// Get current language for navigation
					const currentLanguage = localStorage.getItem('i18nextLng') || 'en';
					const adminPath = `/${currentLanguage}/admin`;

					// Redirect to admin page
					setTimeout(() => {
						window.location.href = adminPath;
					}, 100);

					return { accessToken, refreshToken, error: null, isAdmin: true };
				}
			} catch (tokenError) {
				console.error('Error decoding token:', tokenError);
				// If we can't decode the token, continue with normal flow
			}

			// Save tokens and revalidate SWR for non-admin users
			setAccessToken(accessToken, refreshToken);

			return { accessToken, refreshToken, error: null, isAdmin: false }; // Ensure consistent return structure
		} catch (err: any) {
			const errorKey = err.response?.data?.error || err.response?.data?.message || 'auth.login_failed';

			setError(errorKey);

			return { accessToken: null, refreshToken: null, error: errorKey, isAdmin: false }; // Ensure consistent return structure
		} finally {
			setIsLoading(false);
		}
	};

	return { login, isLoading, error };
}

export function useLoginWithGoogle() {
	const { setAccessToken } = useAuthStore();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const loginWithGoogle = async (code: string) => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await loginWithGoogleService(code);

			const { accessToken, refreshToken } = response.data;

			// Save tokens and revalidate SWR
			setAccessToken(accessToken, refreshToken);
			mutate('/auth/me');

			return { accessToken, refreshToken, error: null }; // Ensure consistent return structure
		} catch (err: any) {
			const errorKey = err.response?.data?.error || err.response?.data?.message || 'auth.login_failed';

			setError(errorKey);

			return { accessToken: null, refreshToken: null, error: errorKey, isAdmin: false }; // Ensure consistent return structure
		} finally {
			setIsLoading(false);
		}
	};

	return { loginWithGoogle, isLoading, error };
}

export function useRegisterWithGoogle() {
	const { setAccessToken } = useAuthStore();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const registerWithGoogle = async ({ role, code }: { role: string; code: string }) => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await registerWithGoogleService({ role, code });
			const { access_token } = response.data;

			setAccessToken(access_token);
			mutate('auth/me');

			return response.data;
		} catch (error) {
			const errorKey = error.response?.data?.error || error.response?.data?.message || 'auth.registration_failed';
			setError(errorKey);
		}
	};

	return { registerWithGoogle, isLoading, error };
}

export function useRegister() {
	const { setAccessToken } = useAuthStore();
	const { t } = useTranslation();

	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const register = async (payload: RegisterPayload) => {
		setIsLoading(true);
		setError(null);
		try {
			const response = await registerService(payload);
			const { refreshToken, accessToken } = response.data;

			setAccessToken(accessToken, refreshToken);
			mutate('users');

			return response.data;
		} catch (error) {
			const errorKey = error.response?.data?.error || error.response?.data?.message || 'auth.registration_failed';
			
			setError(errorKey);
			showSnackbar(t('error'), t(errorKey), 'error');

			throw new Error('Register Failed');
		} finally {
			setIsLoading(false);
		}
	};

	return { register, isLoading, error };
}

export function useLogout() {
	const { logout } = useAuthStore();

	const logoutUser = async () => {
		const refreshToken = localStorage.getItem('refreshToken');

		if (refreshToken) {
			try {
				await logoutService(refreshToken);
			} catch (error) {
				// If logout service fails, we still want to clean up locally
				console.warn('Logout service failed, but continuing with local cleanup:', error);
			}
		}

		clearUserCache();

		// TODO: Remove this when backend is ready
		Cookies.remove('can_switch_role');

		logout();
		mutate('auth/me', null, false);
	};

	return { logout: logoutUser };
}

export function useRefreshToken() {
	const { setAccessToken } = useAuthStore();

	const refreshToken = async () => {
		const storedRefreshToken = localStorage.getItem('refreshToken');

		if (!storedRefreshToken) {
			throw new Error('No refresh token found');
		}

		try {
			const response = await refreshTokenService(storedRefreshToken);

			// Update auth store (this will also update cookies and localStorage)
			setAccessToken(response.accessToken, storedRefreshToken);

			return { accessToken: response.accessToken, refreshToken: storedRefreshToken };
		} catch (error) {
			// Clear tokens on refresh failure
			useAuthStore.getState().logout();
			throw error;
		}
	};

	return { refreshToken };
}

export function useForgotPassword() {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const forgotPassword = async (email: string) => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await forgotPasswordService(email);
			return { data: response.data, error: null };
		} catch (err: any) {
			const errorMessage = err.response?.data?.error || 'Unknown error';
			setError(errorMessage);
			return { data: null, error: errorMessage };
		} finally {
			setIsLoading(false);
		}
	};

	return { forgotPassword, isLoading, error };
}

export function useResetPasswordWithToken() {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const resetPasswordWithToken = async (password: string, token: string) => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await resetPasswordWithTokenService(password, token);
			return { success: true, data: response.data, error: null };
		} catch (err: any) {
			const errorMessage = err.response?.data?.error || 'Password reset failed';
			setError(errorMessage);
			return { success: false, data: null, error: errorMessage };
		} finally {
			setIsLoading(false);
		}
	};

	return { resetPasswordWithToken, isLoading, error };
}

export function useValidateResetPasswordToken() {
	const [isLoading, setIsLoading] = useState(true);
	const [isValid, setIsValid] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const validateToken = async (token: string) => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await validateResetPasswordTokenService(token);
			setIsValid(response.data.isValid);
			return { valid: response.data.isValid, error: null };
		} catch (err: any) {
			const errorMessage = err.response?.data?.error || 'Token validation failed';
			setError(errorMessage);
			setIsValid(false);
			return { valid: false, error: errorMessage };
		} finally {
			setIsLoading(false);
		}
	};

	return { validateToken, isLoading, isValid, error };
}
